import React, { createContext, useEffect, useState, useRef } from "react";
import Services from "./services/Services";
// import { useGoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { useToaster } from "./ToasterProvider";
import { useLanguage } from "./LanguageProvider";
import LoadingOverlay from "./components/LoadingOverlay/LoadingOverlay";
import { setRedirectSuppress, isRedirectSuppressed } from "./utils/authChannel";
import OnboardingVideoModal from "./components/OnboardingVideoModal/OnboardingVideoModal";

const GeneralContext = createContext();

const GeneralProvider = ({ children }) => {
  const { getTranslation } = useLanguage();
  const { toastError, toastSuccess, toastWarning } = useToaster();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [subscriptionType, setSubscriptionType] = useState("social-hive");
  const [subscriptionToggle, setSubscriptionToggle] = useState(false);
  const [shChannelUri, setShChannelUri] = useState("");
  const [dmRoomUri, setDmRoomUri] = useState("");
  const [eventIDUri, setEventIDUri] = useState("");
  const [eventUriTab, setEventUriTab] = useState(null);
  const [projects, setProjects] = useState([]);
  const tempOpenedWindows = useRef([]);
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedProjectID, setSelectedProjectID] = useState("");
  const [selectedProjectTitle, setSelectedProjectTitle] = useState("");
  const [selectedApi, setSelectedApi] = useState({});
  const [subProjects, setSubProjects] = useState([]);
  // collection
  const [collections, setCollections] = useState([]);
  const [addToCollectionId, setAddToCollectionId] = useState("");
  // documentation
  const [selectedDocumentation, setSelectedDocumentation] = useState({});
  const [bearerToken, setBearerToken] = useState();
  const [selectedBoard, setSelectedBoard] = useState({});
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedDocumentationID, setSelectedDocumentationID] = useState("");
  // kronos
  const [activeKronos, setActiveKronos] = useState(null);
  const [timerInterval, setTimerInterval] = useState(null);
  const [kronosRefreshCounter, setKronosRefreshCounter] = useState(0);
  // collaborators
  const [collaborators, setCollaborators] = useState([]);
  const [receivedInvitations, setReceivedInvitations] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]);

  const [receivedProjectInvitations, setReceivedProjectInvitations] = useState(
    []
  );
  const [sentProjectInvitations, setSentProjectInvitations] = useState([]);
  // panels
  const [panels, setPanels] = useState([]);
  const [selectedTaskByKronos, setSelectedTaskByKronos] = useState("");
  const [openedWindows, setOpenedWindows] = useState([]);
  const [createProjectLoading, setCreateProjectLoading] = useState(false);
  const [panelSelectedYear, setPanelSelectedYear] = useState(
    new Date().getFullYear()
  );
  const [panelSelectedMonth, setPanelSelectedMonth] = useState(
    new Date().getMonth()
  );
  // collaborators
  const [users, setUsers] = useState([]);

  const [emailInvitationCode, setEmailInvitationCode] = useState("");
  const [emailInvitationAcceptReject, setEmailInvitationAcceptReject] = useState(0);
  const [emailInvitationType, setEmailInvitationType] = useState("");
  const [myNotes, setMyNotes] = useState([]);
  const [unfolderedNotes, setUnfolderedNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [recentNotes, setRecentNotes] = useState([]);

  const safeParse = (str) => {
    try { return str ? JSON.parse(str) : null; } catch { return null; }
  };

  const sessionToken = sessionStorage.getItem("token");
  const localToken = localStorage.getItem("token");
  const sessionUser = safeParse(sessionStorage.getItem("user"));
  const localUser = safeParse(localStorage.getItem("user"));
  const useToken = token || localToken || sessionToken;

  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(false);
  const [subscriptionRenewDate, setSubscriptionRenewDate] = useState("");

  const [myChannels, setMyChannels] = useState([]);
  const [channelRooms, setChannelRooms] = useState([]);
  const [orderedRooms, setOrderedRooms] = useState([]);
  const [suppressInvitationPolling, setSuppressInvitationPolling] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  const isAuthenticated = !!sessionToken || !!localToken;

  const userId = sessionUser ? sessionUser?.id : localUser?.id;
  const userInformation = sessionUser ? sessionUser : localUser;

  useEffect(() => {
    if (!userInformation) {
      try {
        const raw = localStorage.getItem("auth_sync");
        const last = safeParse(raw); // mevcut helper'ınız
        // Beklenen payload: { type:'LOGIN', token, user, suppressRedirect?, ts }
        if (last?.type === "LOGIN" && last.token && last.user) {
          // 1) Tazelik: eski mesajlara tepki vermeyelim (örn. 15 sn)
          const fresh = typeof last.ts === 'number' ? (Date.now() - last.ts) < 15000 : true;
          if (!fresh) return;

          // 2) Eğer suppressRedirect bayrağı varsa veya zaten global baskı aktifse,
          //    bu sekmede de TTL kadar blokaj kur (ör. 10 sn)
          if (last.suppressRedirect || isRedirectSuppressed()) {
            setRedirectSuppress(10000);
          }

          // 3) Diğer akışlar bozulmasın diye session storage'ı güncelle
          sessionStorage.setItem("token", last.token);
          sessionStorage.setItem("user", JSON.stringify(last.user));
        }
      } catch { }
    }
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (storedToken) setToken(storedToken);
    if (userId) {
      getAllProjects(storedToken);
    }
  }, [userId]);

  // Check for first login and show onboarding modal
  const hasCheckedFirstLoginRef = useRef(false);
  const lastUserIdRef = useRef(null);
  useEffect(() => {
    const currentUserId = userInformation?.id;
    // Reset check when user changes
    if (currentUserId !== lastUserIdRef.current) {
      hasCheckedFirstLoginRef.current = false;
      lastUserIdRef.current = currentUserId;
    }
    
    if (userInformation && currentUserId && !hasCheckedFirstLoginRef.current) {
      const firstLogin = userInformation.is_first_login === true || userInformation.isFirstLogin === true;
      setIsFirstLogin(firstLogin);
      if (firstLogin) {
        setShowOnboardingModal(true);
        hasCheckedFirstLoginRef.current = true;
      }
    }
  }, [userId, userInformation]);

  const [selectedGame, setSelectedGame] = useState(null);
  const [gameSessionID, setGameSessionID] = useState("");
  const gamePingIntervalRef = useRef(null);
  const [isGameRunning, setIsGameRunning] = useState(false);

  const shAnalyticsRef = useRef({ visitedEventIDs: new Set(), sessionId: Date.now() });
  const initialSearchRef = useRef(typeof window !== "undefined" ? window.location.search : "");
  const [campaignParams, setCampaignParams] = useState({});

  const resetShAnalytics = () => {
    shAnalyticsRef.current = { visitedEventIDs: new Set(), sessionId: Date.now() };
  };

  useEffect(() => {
    const parsed = extractCampaignParamsFromSearch(initialSearchRef.current);
    setCampaignParams(parsed);
  }, []);

  function extractCampaignParamsFromSearch(searchStr) {
    try {
      const params = new URLSearchParams(searchStr || "");
      const source = params.get("source") || undefined;
      const createdBy = params.get("created_by") || params.get("createdBy") || undefined;

      return { source, createdBy };
    } catch {
      return {};
    }
  }

  const buildUserDetails = (isNavigation, extra = {}) => {
    const ua = navigator.userAgent || "";
    const isMobile = /Mobi|Android/i.test(ua);
    const isTablet = /iPad|Tablet/i.test(ua);
    const isPC = !isMobile && !isTablet;
    const os = navigator.platform || "";
    const version = navigator.appVersion || "";
    const appVersion = process.env.REACT_APP_VERSION;
    const windowSize = `${window.innerWidth}x${window.innerHeight}`;
    const browserType = ua.includes("Edg") ? "Edge"
      : ua.includes("Chrome") ? "Chrome"
        : ua.includes("Safari") ? "Safari"
          : ua.includes("Firefox") ? "Firefox" : "Unknown";
    const theme = localStorage.getItem("app-theme");
    // campaignParams -> varsayılan; extra ile gelenler öncelikli olsun

    return {
      userID: userId,
      userAgent: ua,
      os,
      version,
      windowSize,
      isMobile,
      isPC,
      isTablet,
      browserType,
      theme,
      isNavigation,
      appVersion,
      ...campaignParams,
      ...extra,
    };
  };

  const sendEventVisit = (channelID, eventID, userDetails) => {
    setEventUriTab(null)
    return Services.CreateEventVisit(useToken, channelID, eventID, userDetails).catch(() => { });
  };

  const startGamePing = (gameID, sessionID) => {
    if (gamePingIntervalRef.current) clearInterval(gamePingIntervalRef.current);

    gamePingIntervalRef.current = setInterval(() => {
      Services.PlayerSetOnlineInGame(useToken, gameID, sessionID)
        .then(() => { })
        .catch((err) => {
          console.error("Ping hatası", err);
        });
    }, 15000);
  };

  const stopGamePing = () => {
    if (gamePingIntervalRef.current) {
      clearInterval(gamePingIntervalRef.current);
      gamePingIntervalRef.current = null;
    }
  };

  const startGame = async (game) => {
    try {
      const res = await Services.GetGameSessionID(useToken, game.gameID);
      const session = res.data.result.sessionID;
      setGameSessionID(session);
      setSelectedGame(game);
      startGamePing(game.gameID, session);
      setIsGameRunning(true); // ✅ ping başladı
    } catch (err) {
      console.error("Session alma hatası", err);
    }
  };

  const endGame = () => {
    if (isGameRunning) {
      stopGamePing();
      setSelectedGame(null);
      setGameSessionID("");
      setIsGameRunning(false); // ✅ artık çalışmıyor
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const stillOpen = openedWindows.find(
        (win) => win.name === "Games" && win.isActive
      );
      if (!stillOpen && isGameRunning) {
        endGame();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [openedWindows, isGameRunning]);

  const getToken = async () => {
    try {
      const res = await Services.GetToken();
      if (res.data.status) {
        setToken(res.data.result.token);
        return res.data.result.token;
      }
    } catch (err) {
      console.error("Failed to fetch token:", err);
    }
    return null;
  };

  const handleTokenUpdate = (newToken) => {
    if (newToken) {
      setToken(newToken);
      localStorage.setItem("token", newToken);
    }
  };

  useEffect(() => {
    try {
      Services.IsUserExpired(useToken).then((res) => {
        if (res.data.status) {
          setIsSubscriptionExpired(true);
        } else {
          setIsSubscriptionExpired(false);
        }
      });
    } catch (err) {
      console.error("Failed to fetch token:", err);
    }
  }, []);

  useEffect(() => {
    try {
      Services.GetUserSubscriptionInfos(useToken).then((res) => {
        if (res.data.status) {
          setSubscriptionRenewDate(res.data.result.subscriptionExpiredDate);
          setSubscriptionType(res.data.result.subscriptionPackageType);
        } else {
        }
      });
    } catch (err) {
      console.error("Failed to fetch token:", err);
    }
  }, [subscriptionToggle]);

  // openedWindows güncellendiğinde handleUpdateWindows çağrılır
  // useEffect(() => {
  //   handleUpdateOpenedWindows(openedWindows);
  // }, [openedWindows]);

  const handleDeleteProject = async (projectId) => {
    try {
      const res = await Services.DeleteProject(useToken, projectId);
      if (res.data.status) {
        await getAllProjects(useToken);

        const filteredWindows = openedWindows.filter(
          (win) => win.projectId !== projectId
        );
        handleUpdateOpenedWindows(filteredWindows);

        setTabs((prevTabs) => {
          const updatedTabs = prevTabs.filter(
            (tab) => tab.projectID !== selectedProjectID
          );

          if (selectedProjectID === projectId) {
            if (updatedTabs.length > 0) {
              const prevIndex = prevTabs.findIndex(
                (tab) => tab.projectID === selectedProjectID
              );
              const newSelectedIndex = Math.max(0, prevIndex - 1);
              const newSelectedTab = updatedTabs[newSelectedIndex];
              setSelectedTab(newSelectedIndex);

              const newSelectedProject = projects.find(
                (project) => project.projectID === newSelectedTab.projectID
              );

              if (newSelectedProject) {
                handleSelectProject(
                  newSelectedProject.projectID,
                  false,
                  false,
                  newSelectedProject.projectTitle
                );
                setSelectedProjectID(newSelectedProject.projectID);
                setSelectedProjectTitle(newSelectedProject.projectTitle);
              } else {
                setSelectedTab("");
                setSelectedProjectID("");
                setSelectedProjectTitle("");
              }
            } else {
              setSelectedTab("");
              setSelectedProjectID("");
              setSelectedProjectTitle("");
            }
          }
          return updatedTabs;
        });

        toastSuccess(getTranslation("successMessage"));
      } else {
        toastError(getTranslation("somethingWentWrong"));
      }
    } catch (err) {
      console.error("Error deleting project:", err);
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const handleDeleteSubProject = async (subProjectID) => {
    setCreateProjectLoading(true);
    try {
      const res = await Services.DeleteSubProject(useToken, subProjectID);
      if (res.data.status) {
        await getAllProjects(useToken);
        toastSuccess(getTranslation("successMessage"));
      } else {
        toastError(getTranslation("somethingWentWrong"));
        setCreateProjectLoading(false);
      }
    } catch (err) {
      console.error("Error deleting project:", err);
      toastError(getTranslation("somethingWentWrong"));
      setCreateProjectLoading(false);
    }
  };

  // const handleGoogleLogin = useGoogleLogin({
  //   onSuccess: async (res) => {
  //     try {
  //       const userInfoResponse = await Services.GetGoogleUser(res.access_token);

  //       const user = {
  //         name: userInfoResponse.data.given_name || "N/A",
  //         lastname: userInfoResponse.data.family_name || "N/A",
  //         email: userInfoResponse.data.email || "N/A",
  //         picture: userInfoResponse.data.picture || "",
  //       };

  //       // Token kontrolü (Token yoksa çek)
  //       let currentToken = token;
  //       if (!currentToken) {
  //         currentToken = await getToken();
  //       }

  //       const loginRes = await Services.UserLoginWithThirdParty(
  //         currentToken,
  //         "google",
  //         user,
  //         res.access_token
  //       );

  //       if (loginRes.data.status) {
  //         localStorage.setItem("user", JSON.stringify(loginRes.data.result));
  //         localStorage.setItem("token", loginRes.data.result.token);
  //         handleTokenUpdate(loginRes.data.result.token);
  //         if (loginRes.data.result.selectedProject) {
  //           handleSelectProject(
  //             loginRes.data.result.selectedProject,
  //             loginRes.data.result.token,
  //             loginRes.data.result.id
  //           );
  //         }
  //         navigate("/home");
  //       } else {
  //         console.error("Google Login Failed: ", loginRes.data);
  //       }
  //     } catch (err) {
  //       console.error("Error during Google Login: ", err);
  //     }
  //   },
  //   onError: (err) => {
  //     console.error("Google Login Failed: ", err);
  //   },
  // });

  const handleGoogleLogin = () => {

  }

  const handleGithubLogin = () => {
    const clientId = "Ov23li6adS5sY7ykF2zW";
    const redirectUri = encodeURIComponent("https://panel.lodos.io");

    window.location.href = `https://github.com/login/oauth/authorize?client_id=${clientId}&scope=read:user,user:email&redirect_uri=${redirectUri}`;
  };

  const fetchGithubUser = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (code) {
      try {
        // Token Alımı (Direct GitHub API Call)
        const tokenResponse = await Services.GetGithubToken(code);

        const { access_token } = tokenResponse.data;

        const userResponse = await Services.GetGithubUser(access_token);

        const emailResponse = await Services.GetGithubEmails(access_token);

        const primaryEmail = emailResponse.data.find(
          (email) => email.primary
        ).email;

        const user = {
          name: userResponse.data.name,
          email: primaryEmail,
          picture: userResponse.data.avatar_url,
        };

        let currentToken = token;
        if (!currentToken) {
          currentToken = await getToken();
        }

        const loginRes = await Services.UserLoginWithThirdParty(
          currentToken,
          "github",
          user,
          access_token
        );

        if (loginRes.data.status) {
          localStorage.setItem("user", JSON.stringify(loginRes.data.result));
          localStorage.setItem("token", loginRes.data.result.token);
          handleTokenUpdate(loginRes.data.result.token);
          if (loginRes.data.result.selectedProject) {
            handleSelectProject(
              loginRes.data.result.selectedProject,
              loginRes.data.result.token,
              loginRes.data.result.id
            );
          }
          window.history.replaceState({}, document.title, "/");
          navigate("/home");
        } else {
          console.error("Github Login Failed: ", loginRes.data);
          window.history.replaceState({}, document.title, "/"); // URL'deki code parametresini temizle
        }
      } catch (error) {
        console.error("Error during GitHub OAuth:", error);
      }
    }
  };

  useEffect(() => {
    fetchGithubUser();
  }, []);

  useEffect(() => {
    if (selectedProjectID) {
      getAllPanels(selectedProjectID);
    }
  }, [panelSelectedMonth, panelSelectedYear]);

  useEffect(() => {
    setToken(localToken ? localToken : sessionToken);
    getAllCollections(localToken ? localToken : sessionToken);
    handleGetCollaborators();
    getMyChannels();
    if (selectedProjectID) {
      getAllPanels(selectedProjectID);
    }

    handleGetReceivedInvitations();
    handleGetRecievedProjectInvitations();

    // eslint-disable-next-line
  }, [localToken, sessionToken, selectedProjectID]);

  useEffect(() => {
    if (suppressInvitationPolling) return;
    const intervalId = setInterval(() => {
      handleGetReceivedInvitations();
      handleGetRecievedProjectInvitations();
    }, 150000);

    return () => clearInterval(intervalId);
  }, [useToken, suppressInvitationPolling]);

  useEffect(() => {
    const storedToken =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
    }

    if (userId) {
      getAllProjects(storedToken);
    }
  }, [userId]);

  // useEffect(() => {
  //   if (userId) {
  //     handleGetOpenedWindows();
  //   }
  // }, [userId]);

  useEffect(() => {
    fetchOpenedWindows();
  }, []);

  useEffect(() => {
    if (activeKronos && activeKronos.startTime) {
      const updateTitle = () => {
        const elapsedTime = Math.floor(
          (Date.now() - activeKronos.startTime) / 1000
        );
        const hours = Math.floor(elapsedTime / 3600)
          .toString()
          .padStart(2, "0");
        const minutes = Math.floor((elapsedTime % 3600) / 60)
          .toString()
          .padStart(2, "0");
        const seconds = (elapsedTime % 60).toString().padStart(2, "0");
        document.title = `${hours}:${minutes}:${seconds} - ${activeKronos.title}`;
      };

      updateTitle();
      const interval = setInterval(updateTitle, 1000);
      setTimerInterval(interval);

      return () => {
        clearInterval(interval);
        document.title = "lodos.";
      };
    } else {
      document.title = "lodos.";
    }
  }, [activeKronos]);

  useEffect(() => {
    async function initializeActiveKronos() {
      try {
        const currentKronos = await handleGetCurrentKronos();
        if (currentKronos?.status) {
          const {
            kronosTitle,
            startDateTime,
            projectTitle,
            logId,
            kronosId,
            taskId,
          } = currentKronos.result;

          setActiveKronos({
            title: kronosTitle,
            startTime: new Date(startDateTime * 1000).getTime(),
            projectTitle: projectTitle,
            kronosId: kronosId,
            logId: logId,
            taskId: taskId ? taskId : "",
          });
        }
      } catch (error) {
        console.error("Error initializing active Kronos:", error);
      }
    }

    initializeActiveKronos();
  }, [selectedProjectID]);

  // projects
  const getAllProjects = async (inFuncToken) => {
    let useFuncToken = inFuncToken ? inFuncToken : useToken;
    try {
      const res = await Services.GetUserAuthorizations(useFuncToken, userId);
      if (res.data.status) {
        setCreateProjectLoading(false);
        setProjects(res.data.result);
        if (res.data.result.length === 1) {
          await handleSelectProject(
            res.data.result[0].projectID,
            useToken,
            false,
            res.data.result[0].projectTitle
          );
          const formattedSubProjects = res.data.result[0]?.subProjects?.map(
            (sub) => ({
              value: sub.subProjectId,
              label: sub.title,
            })
          );
          setSubProjects(formattedSubProjects);
        } else {
          const selectedProject = res.data.result?.find(
            (project) => project.projectID === selectedProjectID
          );
          if (selectedProject) {
            const formattedSubProjects = selectedProject.subProjects?.map(
              (sub) => ({
                value: sub.subProjectId,
                label: sub.title,
              })
            );
            setSubProjects(formattedSubProjects);
          } else {
            setSubProjects([]);
          }
        }
      } else {
        setCreateProjectLoading(false);
        setProjects([]);
      }
    } catch (err) {
      setCreateProjectLoading(false);
      setProjects([]);
      console.log(err);
    }
  };

  const handleUpdateProjectTitle = (projectId, newTitle) => {
    Services.UpdateProject(useToken, projectId, newTitle)
      .then((res) => {
        if (res.data.status) {
          getAllProjects();

          setTabs((prevTabs) =>
            prevTabs?.map((tab) =>
              tab.projectID === selectedProjectID
                ? { ...tab, projectTitle: newTitle }
                : tab
            )
          );

          if (selectedProjectID === projectId) {
            setSelectedProjectTitle(newTitle);
          }

          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleUpdateSubProject = (subProjectID, updateData) => {
    Services.UpdateSubProject(useToken, subProjectID, updateData)
      .then((res) => {
        if (res.data.status) {
          getAllProjects();
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleAddNewProject = async (title, description) => {
    const userID = sessionUser ? sessionUser?.id : localUser?.id;
    try {
      const res = await Services.CreateProject(useToken, title, description);
      setCreateProjectLoading(true);
      if (res.data.status) {
        let authObj = {
          type: "owner",
          authorizations: "all",
          canSeeKronos: true,
        };
        await handleCreateAuthorization(
          userID,
          res.data.result.projectId,
          authObj
        );
        const newProject = res.data.result;
        await handleSelectProject(
          newProject.projectId,
          useToken,
          false,
          newProject.title
        );
        await getAllProjects(useToken);
        return newProject;
      } else {
        setCreateProjectLoading(false);
        console.error(res.data);
        return null;
      }
    } catch (err) {
      setCreateProjectLoading(false);
      console.error(err);
      return null;
    }
  };

  const handleAddSubProject = async (title, description) => {
    try {
      const res = await Services.CreateSubProject(
        useToken,
        selectedProjectID,
        title,
        description
      );
      setCreateProjectLoading(true);
      if (res.data.status) {
        toastSuccess(getTranslation("successMessage"));
        await getAllProjects(useToken);
      } else {
        toastError(getTranslation("somethingWentWrong"));
        setCreateProjectLoading(false);
        console.error(res.data);
      }
    } catch (err) {
      toastError(getTranslation("somethingWentWrong"));
      setCreateProjectLoading(false);
      console.error(err);
    }
  };

  const handleSelectProject = async (
    projectId,
    inFuncToken,
    thirdPartyUser = false,
    title = "",
    user = false
  ) => {
    setCreateProjectLoading(true);
    let useFuncToken = inFuncToken ? inFuncToken : useToken;
    const userID = thirdPartyUser
      ? thirdPartyUser
      : sessionUser
        ? sessionUser?.id
        : localUser?.id;
    try {
      const res = await Services.SetUserSelectedProjectId(
        useFuncToken,
        user ? user : userID,
        projectId
      );
      if (res.data.status) {
        setSelectedProjectTitle(title);
        setSelectedProjectID(projectId);
        setSelectedDocumentation({});
        setSelectedApi({});
        setSelectedBoard({});
        await getAllPanels(projectId);
      } else {
        setCreateProjectLoading(false);
        setSelectedProjectID("");
        setSelectedProjectTitle("");
      }
    } catch (err) {
      setCreateProjectLoading(false);
      setSelectedProjectID("");
      setSelectedProjectTitle("");
      console.log(err);
    }
  };

  // Panels
  const handleAddNewPanel = (title) => {
    Services.GetUserAuthorizations(token, userId)
      .then((res) => {
        if (res.data.status) {
          let myAuth = res.data.result.find(
            (auth) => auth.projectID === selectedProjectID
          ).authorizations;

          Services.CreatePanel(useToken, title, selectedProjectID)
            .then((res) => {
              if (res.data.result) {
                if (myAuth.type === "owner") {
                  getAllPanels();
                } else {
                  if (myAuth.authorizations.panels !== "all") {
                    let authObj = {
                      id: res.data.result.id,
                      authorization: "all",
                    };
                    myAuth.authorizations.panels.push(authObj);
                    Services.UpdateCollaboratorAuthorizations(
                      token,
                      userId,
                      selectedProjectID,
                      myAuth
                    )
                      .then((res) => {
                        if (res.data.status) {
                          getAllPanels();
                        } else {
                          toastError(getTranslation("somethingWentWrong"));
                        }
                      })
                      .catch((err) => {
                        console.log(err);
                      });
                  } else {
                    getAllPanels();
                  }
                }
              } else {
                toastError(getTranslation("somethingWentWrong"));
              }
            })
            .catch((err) => {
              console.log(err);
              toastError(getTranslation("somethingWentWrong"));
            });
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleUpdatePanelTitle = (panelId, newName) => {
    Services.UpdatePanel(token, panelId, newName)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleDeletePanel = (panelId) => {
    Services.DeletePanel(token, panelId)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  function formatDateLocal(date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const getAllPanels = async (projectId = "") => {
    const start = new Date(panelSelectedYear, panelSelectedMonth, 1);
    const end = new Date(panelSelectedYear, panelSelectedMonth + 1, 0);

    const startDate = formatDateLocal(start);
    const endDate = formatDateLocal(end);

    try {
      const res = await Services.GetMyPanels(
        useToken,
        projectId !== "" ? projectId : selectedProjectID,
        startDate,
        endDate
      );
      if (res.data.status) {
        setPanels(res.data.result);
        setCreateProjectLoading(false);
      } else {
        setPanels([]);
        setCreateProjectLoading(false);
      }
    } catch (err) {
      console.log(err);
      setPanels([]);
    }
  };
  // Boards
  const handleNewAddBoard = (panelId, title) => {
    let members = [{ id: userId, permission: "admin" }];
    Services.CreateBoard(useToken, panelId, title, members, selectedProjectID)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const handleDeleteBoard = (boardId) => {
    Services.DeleteBoard(useToken, boardId)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const handleBoardUpdate = (boardId, updateData) => {
    Services.UpdateBoard(useToken, boardId, updateData)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  // tasks
  const handleAddNewTask = (body) => {
    Services.CreateTask(useToken, body)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const handleUpdateTask = (taskId, body) => {
    Services.UpdateTask(useToken, taskId, body)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const handleDeleteTask = (taskId) => {
    Services.DeleteTask(useToken, taskId)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const handleAddMemberToTask = (members, taskId) => {
    Services.AddUserToTask(useToken, members, taskId)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const handleRemoveMemberFromTask = (userID, taskId) => {
    Services.RemoveUserFromTask(useToken, userID, taskId)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const handleWriteComment = (
    taskId,
    comment,
    attachment,
    repliedCommentId
  ) => {
    Services.WriteComment(
      useToken,
      taskId,
      comment,
      attachment,
      repliedCommentId
    )
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        toastError(getTranslation("somethingWentWrong"));
        console.log(err);
      });
  };

  const handleDeleteComment = (taskCommentId) => {
    Services.DeleteComment(useToken, taskCommentId)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        toastError(getTranslation("somethingWentWrong"));
        console.log(err);
      });
  };

  const handleDeleteAttachment = (attachmentId) => {
    Services.DeleteAttachment(useToken, attachmentId)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        toastError(getTranslation("somethingWentWrong"));
        console.log(err);
      });
  };

  const handleDeleteCommentAttachment = (attachmentId) => {
    Services.DeleteCommentAttachment(useToken, attachmentId)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        toastError(getTranslation("somethingWentWrong"));
        console.log(err);
      });
  };

  const handleAddTaskAttachment = (taskId, attachment) => {
    Services.AddTaskAttachment(useToken, taskId, attachment)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        toastError(getTranslation("somethingWentWrong"));
        console.log(err);
      });
  };

  const handleAddCommentAttachment = (taskId, taskCommentId, attachment) => {
    Services.AddCommentAttachment(useToken, taskId, taskCommentId, attachment)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        toastError(getTranslation("somethingWentWrong"));
        console.log(err);
      });
  };

  const handleUpdateComment = (taskCommentId, updateData) => {
    Services.UpdateComment(useToken, taskCommentId, updateData)
      .then((res) => {
        if (res.data.status) {
          getAllPanels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        toastError(getTranslation("somethingWentWrong"));
        console.log(err);
      });
  };

  // Collaborators
  const handleInviteCollaborators = async (emails) => {
    let counter = 0;
    let specialErrorMessage = "";

    const invitePromises = emails.map((email, index) => {
      return Services.InviteCollabrators(token, email, "collaborator")
        .then((res) => {
          if (
            res.data &&
            typeof res.data.message === "string" &&
            res.data.message.includes(
              "You can not send an invitation to yourself"
            )
          ) {
            specialErrorMessage = "You can not send an invitation to yourself";
          } else if (res.data.status) {
            counter++;
          }
        })
        .catch((err) => {
          console.error(err);
        });
    });

    await Promise.all(invitePromises);

    handleGetSentInvitations();

    if (specialErrorMessage) {
      return { success: false, specialMessage: specialErrorMessage };
    } else if (emails?.length === counter) {
      return { success: true };
    } else {
      return { success: false };
    }
  };

  const handleRemoveCollaborator = (memberID) => {
    Services.RemoveUserFromCollaborators(useToken, memberID)
      .then((res) => {
        if (res.data.status) {
          handleGetCollaborators();
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        setCollaborators([]);
      });
  };

  const handleCancelCollaboratorInvitation = (invitationCode) => {
    Services.CancelCollaboratorInvitation(useToken, invitationCode)
      .then((res) => {
        if (res.data.status) {
          handleGetSentInvitations();
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        setCollaborators([]);
      });
  };

  const handleGetCollaborators = () => {
    // XYZ - Buraya başkası onayladığında da görünmeli
    Services.GetCollaborators(useToken)
      .then((res) => {
        if (res.data.status) {
          setCollaborators(res.data.result);
        } else {
          setCollaborators([]);
        }
      })
      .catch((err) => {
        console.log(err);
        setCollaborators([]);
      });
  };

  const getCollaboratorsByProjectID = async () => {
    try {
      const response = await Services.GetCollaboratorsByProjectID(
        useToken,
        selectedProjectID
      );
      if (response.data.status) {
        return response.data;
      } else {
        return { status: false };
      }
    } catch (error) {
      return { status: false };
    }
  };

  const fetchMembers = async () => {
    const response = await getCollaboratorsByProjectID();
    if (response.status) {
      let usersWithoutSelf = response.result.filter(
        (user) => user.id !== userId
      );
      const processedUsers = usersWithoutSelf.map((user) => {
        // Kullanıcı bilgilerini işliyoruz
        const userNameLastname = `${user.name} ${user.lastname}`;
        return {
          ...user, // Mevcut user objesi olduğu gibi korunuyor
          userNameLastname,
          status: true, // Varsayılan olarak true
        };
      });
      setUsers(processedUsers);
    } else {
      setUsers([]);
    }
  };

  const updateCollaboratorsAuthorizations = async (
    memberID,
    authorizations
  ) => {
    try {
      const response = await Services.UpdateCollaboratorAuthorizations(
        useToken,
        memberID,
        selectedProjectID,
        authorizations
      );
      if (response.data.status) {
        toastSuccess(getTranslation("successMessage"));
        fetchMembers();
      } else {
        toastError(getTranslation("somethingWentWrong"));
      }
    } catch (error) {
      console.log(error);
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const getCollaboratorsKronoses = async (collaborators, rangeData) => {
    const data = {
      projectID: selectedProjectID,
      collaborators: collaborators,
      filter: rangeData.type,
      ...(rangeData.type === "inBetween" && {
        startDate: rangeData.start,
        endDate: rangeData.end,
      }),
    };

    try {
      const response = await Services.GetCollaboratorsKronoses(useToken, data);
      if (response.data.status) {
        return response.data;
      } else {
        return { status: false };
      }
    } catch (error) {
      return { status: false };
    }
  };

  const handleGetReceivedInvitations = async () => {
    try {
      const res = await Services.GetReceivedInvitations(
        useToken,
        userInformation.email
      );
      if (res.data.status) {
        setReceivedInvitations(res.data.result);
      } else {
        setReceivedInvitations([]);
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      setReceivedInvitations([]);
    }
  };

  const handleGetRecievedProjectInvitations = async () => {
    try {
      const res = await Services.GetReceivedProjectInvites(useToken);
      if (res.data.status) {
        setReceivedProjectInvitations(res.data.result);
      } else {
        setReceivedProjectInvitations([]);
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      setReceivedProjectInvitations([]);
    }
  };

  const handleGetSentProjectInvitations = async () => {
    try {
      const res = await Services.GetSentProjectInvites(
        useToken,
        selectedProjectID
      );
      if (res.data.status) {
        setSentProjectInvitations(
          res.data.result.filter((x) => x.userID !== userId)
        );
      } else {
        setSentProjectInvitations([]);
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      setSentProjectInvitations([]);
    }
  };

  const handleGetSentInvitations = async () => {
    try {
      const res = await Services.GetSentInvitations(useToken);
      if (res.data.status) {
        setSentInvitations(res.data.result);
      } else {
        setSentInvitations([]);
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      setSentInvitations([]);
    }
  };

  const handleAcceptInvitation = async (invitationCode) => {
    try {
      const res = await Services.AcceptInvitation(useToken, invitationCode);
      if (res.data.status) {
        toastSuccess(getTranslation("successMessage"));
        handleGetReceivedInvitations();
        handleGetCollaborators();
      } else {
        toastError(getTranslation("somethingWentWrong"));
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const handleRejectInvitation = async (invitationCode) => {
    try {
      const res = await Services.RejectInvitation(useToken, invitationCode);
      if (res.data.status) {
        toastSuccess(getTranslation("successMessage"));
        handleGetReceivedInvitations();
      } else {
        toastError(getTranslation("somethingWentWrong"));
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const handleAcceptInvitationWithToken = async (
    token,
    invitationCode,
    email
  ) => {
    try {
      const receivedData = await Services.GetReceivedInvitations(
        useToken,
        email
      );

      if (receivedData.data.status) {
        const res = await Services.AcceptInvitation(token, invitationCode);
        if (res.data.status) {
          toastSuccess(
            getTranslation("collaboratorAdded") +
            receivedData.data.result.find(
              (x) => x.invitationCode === invitationCode
            ).inviterName +
            " " +
            receivedData.data.result.find(
              (x) => x.invitationCode === invitationCode
            ).inviterLastname
          );
          handleGetReceivedInvitations();
          handleGetCollaborators();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const handleRejectInvitationWithToken = async (token, invitationCode) => {
    try {
      const res = await Services.RejectInvitation(token, invitationCode);
      if (res.data.status) {
        toastSuccess(getTranslation("successMessage"));
        handleGetReceivedInvitations();
      } else {
        toastError(getTranslation("somethingWentWrong"));
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const handleAcceptProjectInvitation = async (authID) => {
    try {
      const res = await Services.AcceptProjectInvitation(useToken, authID);
      if (res.data.status) {
        toastSuccess(getTranslation("successMessage"));
        handleGetRecievedProjectInvitations();
        getAllProjects();
      } else {
        toastError(getTranslation("somethingWentWrong"));
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const handleRejectProjectInvitation = async (authID) => {
    try {
      const res = await Services.RejectProjectInvitation(useToken, authID);
      if (res.data.status) {
        toastSuccess(getTranslation("successMessage"));
        handleGetRecievedProjectInvitations();
      } else {
        toastError(getTranslation("somethingWentWrong"));
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const handleAcceptProjectInvitationWithToken = async (token, authID) => {
    try {
      const receivedData = await Services.GetReceivedProjectInvites(useToken);
      if (receivedData.data.status) {
        const res = await Services.AcceptProjectInvitation(token, authID);
        if (res.data.status) {
          toastSuccess(
            getTranslation("joinedProject") +
            receivedData.data.result.find((x) => x.authID === authID)
              .projectTitle
          );
          handleGetRecievedProjectInvitations();
          getAllProjects(token);
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const handleRejectProjectInvitationWithToken = async (token, authID) => {
    try {
      const res = await Services.RejectProjectInvitation(token, authID);
      if (res.data.status) {
        toastSuccess(getTranslation("successMessage"));
        handleGetRecievedProjectInvitations();
      } else {
        toastError(getTranslation("somethingWentWrong"));
      }
    } catch (err) {
      console.error("Error fetching received invitations:", err);
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  // collections
  const getAllCollections = (inFuncToken) => {
    // setIsLoading(true);
    let useFuncToken = inFuncToken ? inFuncToken : useToken;
    Services.GetMyCollections(useFuncToken, selectedProjectID)
      .then((res) => {
        if (res.data.status) {
          setCollections(res.data.result);
        } else {
          setCollections([]);
        }
      })
      .catch((err) => {
        setCollections([]);
        console.log(err);
      });
    // .finally(() => setIsLoading(false));
  };

  const handleAddNewCollection = async (name) => {
    try {
      const authRes = await Services.GetUserAuthorizations(token, userId);

      if (!authRes.data.status) {
        toastError(getTranslation("somethingWentWrong"));
        return null;
      }

      let myAuth = authRes.data.result.find(
        (auth) => auth.projectID === selectedProjectID
      )?.authorizations;

      const userID = sessionUser ? sessionUser?.id : localUser?.id;

      const createRes = await Services.CreateCollection(
        token,
        name,
        userID,
        selectedProjectID
      );

      if (!createRes.data.status) {
        toastError(getTranslation("somethingWentWrong"));
        return null;
      }

      const raw = createRes.data.result;

      // ApiPostGet tarafında hem collectionId hem id üzerinden erişim yapıyorsun,
      // o yüzden ikisini de dolduruyorum.
      const createdCollection = {
        ...raw,
        collectionId: raw.collectionId ?? raw.id,
        collectionTitle: raw.collectionTitle ?? raw.name ?? name,
      };

      // Yetki güncelleme mantığın aynen korunuyor, sadece async/await'e çevrildi
      if (myAuth.type === "owner") {
        await getAllCollections();
      } else {
        if (myAuth.authorizations.collections !== "all") {
          let authObj = {
            id: createdCollection.collectionId,
            authorization: "all",
          };
          myAuth.authorizations.collections.push(authObj);

          const updateRes = await Services.UpdateCollaboratorAuthorizations(
            token,
            userId,
            selectedProjectID,
            myAuth
          );

          if (!updateRes.data.status) {
            toastError(getTranslation("somethingWentWrong"));
            return createdCollection; // collection oluştu, sadece yetki update fail
          }

          await getAllCollections();
        } else {
          await getAllCollections();
        }
      }

      // 🔴 ApiPostGet importFromPostmanJson burada bunu bekliyor:
      // createdCollection?.collectionId || createdCollection?.id
      return createdCollection;
    } catch (err) {
      console.log(err);
      toastError(getTranslation("somethingWentWrong"));
      return null;
    }
  };

  const handleCollaboratorInvitationByEmail = () => {
    if (emailInvitationType === "collaborators") {
      return Services.GetInvitedUserID(useToken, emailInvitationCode)
        .then((res) => {
          if (res.data.status) {
            if (res.data.result === userId) {
              if (emailInvitationAcceptReject === 1) {
                handleAcceptInvitationWithToken(useToken, emailInvitationCode);
              } else if (emailInvitationAcceptReject === 2) {
                handleRejectInvitationWithToken(useToken, emailInvitationCode);
              }
            }
          } else {
          }
        })
        .catch((err) => {
          console.error("Error adding members:", err);
          return false;
        });
    } else if (emailInvitationType === "collaborators-new") {
      return Services.GetInvitedEmail(useToken, emailInvitationCode)
        .then((res) => {
          if (res.data.status) {
            if (res.data.result === userInformation.email) {
              handleAcceptInvitationWithToken(
                useToken,
                emailInvitationCode,
                userInformation.email
              );
            }
          } else {
          }
        })
        .catch((err) => {
          console.error("Error adding members:", err);
          return false;
        });
    } else if (emailInvitationType === "project") {
      return Services.GetInvitedUserIDForProject(useToken, emailInvitationCode)
        .then((res) => {
          if (res.data.status) {
            if (res.data.result === userId) {
              if (emailInvitationAcceptReject === 1) {
                handleAcceptProjectInvitationWithToken(
                  useToken,
                  emailInvitationCode
                );
              } else if (emailInvitationAcceptReject === 2) {
                handleRejectProjectInvitationWithToken(
                  useToken,
                  emailInvitationCode
                );
              }
            }
          } else {
          }
        })
        .catch((err) => {
          console.error("Error adding members:", err);
          return false;
        });
    }
  };

  // members

  const handleAddMembers = (members) => {
    return Services.AddMembers(useToken, selectedProjectID, members)
      .then((res) => {
        if (res.data.status) {
          return true;
        } else {
          return false;
        }
      })
      .catch((err) => {
        console.error("Error adding members:", err);
        return false;
      });
  };

  const handleCreateAuthorization = async (
    userID,
    projectID = selectedProjectID,
    authorizations
  ) => {
    try {
      const res = await Services.CreateUserAuthorization(
        useToken,
        userID,
        projectID,
        authorizations
      );
      return res.data.status ? true : false;
    } catch (err) {
      setCreateProjectLoading(false);
      console.error("Error adding members:", err);
      return false;
    }
  };

  const handleInviteToProject = async (
    userID,
    projectID = selectedProjectID,
    authorizations
  ) => {
    try {
      const res = await Services.InviteToProject(
        useToken,
        userID,
        projectID,
        authorizations
      );
      return res.data.status ? true : false;
    } catch (err) {
      setCreateProjectLoading(false);
      console.error("Error adding members:", err);
      return false;
    }
  };

  const getUserAuthorizations = async (token = useToken, userID = userId) => {
    try {
      const res = await Services.GetUserAuthorizations(token, userID);
      if (res.data.status) {
        return res.data;
      } else {
        return { status: false };
      }
    } catch (err) {
      console.log(err);
      return { status: false };
    }
  };

  const getMembers = async (table, id) => {
    try {
      const res = await Services.GetMembers(useToken, table, id);
      if (res.data.status) {
        return res.data;
      } else {
        return { status: false };
      }
    } catch (err) {
      console.log(err);
      return { status: false };
    }
  };

  const getProjectMembers = async () => {
    try {
      const res = await Services.GetProjectMembers(useToken, selectedProjectID);

      if (res.data.status) {
        return res.data;
      } else {
        return { status: false };
      }
    } catch (err) {
      console.log(err);
      return { status: false };
    }
  };

  // apis
  // GeneralContext içinde
  const handleAddApi = async (api, response, collectionId, options = {}) => {
    const { skipCollectionsRefresh = false, suppressToast = false } = options;

    // Eğer selectedApi boş ise yani yeni API oluşturuluyor
    if (Object.keys(selectedApi).length === 0) {
      const userID = sessionUser ? sessionUser?.id : localUser?.id;
      let members = [{ id: userID, permission: "admin" }];
      console.log(collectionId, "--")
      try {
        const res = await Services.CreateAPI(
          token,
          collectionId,
          api.apiTitle,
          api.method,
          api.url,
          api.auth,
          api.headers,
          api.body,
          members
        );

        if (res.data.status) {
          const receivedData = res.data.result;
          const updatedData = {
            ...receivedData,
            response: response,
          };

          const parameters = {
            body: receivedData.body,
            url: receivedData.url,
            title: receivedData.title,
            method: receivedData.method,
          };

          const docRes = await Services.CreateDocumentation(
            token,
            addToCollectionId,
            receivedData.apiId,
            api.apiTitle,
            parameters
          );

          if (docRes.data.status) {
            // 🔹 Artık her API için değil, istersek tek seferde refresh
            if (!skipCollectionsRefresh) {
              getAllCollections();
            }

            setSelectedApi(updatedData);
            if (!suppressToast) toastSuccess(getTranslation("successMessage"));
            return updatedData; // Başarılıysa oluşturulan API nesnesini return ediyoruz
          } else {
            if (!suppressToast) toastError(getTranslation("somethingWentWrong"));
            return false;
          }
        } else {
          if (!suppressToast) toastError(getTranslation("somethingWentWrong"));
          return false;
        }
      } catch (error) {
        console.log(error);
        return false;
      }
    }
  };

  const handleSelectApi = (api, collectionId) => {
    setAddToCollectionId(collectionId);

    if (api.id === selectedApi.apiId) {
      setSelectedApi({});
    } else {
      let body =
        api.body &&
          JSON.stringify(api.body) !== "{}" &&
          typeof api.body !== "string"
          ? JSON.stringify(api.body)
          : api.body;

      if (api?.body?.length === 0) {
        body = "{}";
      }

      setSelectedApi({
        ...api,
        body,
      });
    }
  };

  const handleUpdateApi = (apiId, updatedApi, collectionId) => {
    // setIsLoading(true);

    const title =
      selectedApi.apiTitle === updatedApi.apiTitle ? null : updatedApi.apiTitle;

    Services.UpdateAPI(
      token,
      collectionId,
      apiId,
      title,
      updatedApi.method,
      updatedApi.url,
      updatedApi.auth,
      updatedApi.headers,
      updatedApi.body
    )
      .then((res) => {
        if (res.data.status) {
          getAllCollections();
          const modifiedResult = {
            ...res.data.result,
            apiTitle: res.data.result.title
              ? res.data.result.title
              : selectedApi.apiTitle,
            apiId: selectedApi.apiId,
          };
          delete modifiedResult.title;
          setSelectedApi(modifiedResult);
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
    // .finally(() => setIsLoading(false));
  };

  const handleUpdateApiTitle = (apiId, collectionId, title) => {
    // setIsLoading(true);

    Services.UpdateAPITitle(token, collectionId, apiId, title)
      .then((res) => {
        if (res.data.status) {
          getAllCollections();
          const modifiedResult = {
            ...res.data.result,
            apiTitle: res.data.result.title
              ? res.data.result.title
              : selectedApi.apiTitle,
            apiId: selectedApi.apiId,
          };
          delete modifiedResult.title;
          setSelectedApi(modifiedResult);
        } else {
          // toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
    // .finally(() => setIsLoading(false));
  };

  const handleCreateApiGroup = async (title, projectId, collectionId, options = {}) => {
    const { skipCollectionsRefresh = false, suppressToast = false } = options;

    try {
      const res = await Services.CreateAPIGroup(token, title, projectId, collectionId);

      if (!res.data.status) {
        if (!suppressToast) toastError(getTranslation("somethingWentWrong"));
        return null;
      }

      const raw = res.data.result;

      const createdGroup = {
        ...raw,
        groupId: raw.groupId ?? raw.id,
        title: raw.title ?? title,
        collectionId,
        apis: raw.apis ?? [],
      };

      if (!skipCollectionsRefresh) {
        await getAllCollections();
      }

      if (!suppressToast) toastSuccess(getTranslation("successMessage"));
      // ApiPostGet içindeki Postman import burada bunu kullanıyor
      return createdGroup;
    } catch (err) {
      console.log(err);
      if (!suppressToast) toastError(getTranslation("somethingWentWrong"));
      return null;
    }
  };

  const handleUpdateApiGroup = (id, title) => {
    Services.UpdateAPIGroup(token, id, title)
      .then((res) => {
        if (res.data.status) {
          getAllCollections();
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleDeleteApiGroup = (id) => {
    Services.DeleteAPIGroup(token, id)
      .then((res) => {
        if (res.data.status) {
          getAllCollections();
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleChangeApiGroup = (collectionId, apiId, groupId, options = {}) => {
    const { skipCollectionsRefresh = false, suppressToast = false } = options;

    return Services.ChangeAPIGroup(token, collectionId, apiId, groupId)
      .then((res) => {
        if (res.data.status) {
          if (!skipCollectionsRefresh) {
            getAllCollections();
          }
          if (!suppressToast) toastSuccess(getTranslation("successMessage"));
        } else {
          if (!suppressToast) toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
    // .finally(() => setIsLoading(false));
  };

  const handleSaveAsApi = (api, response, collectionId) => {
    let members = [{ id: userId, permission: "admin" }];

    // DIKKAT: Burada return ile CreateAPI promise'ini geri döndürüyoruz
    return Services.CreateAPI(
      useToken,
      collectionId,
      api.apiTitle,
      api.method,
      api.url,
      api.auth,
      api.headers,
      api.body,
      members
    )
      .then((res) => {
        if (!res.data.status) {
          toastError(getTranslation("somethingWentWrong"));
          return null; // başarısızsa null dön
        }

        const receivedData = res.data.result;

        const parameters = {
          body: receivedData.body,
          url: receivedData.url,
          title: receivedData.title,
          method: receivedData.method,
        };

        const updatedData = {
          ...receivedData,
          response: response,
        };

        // Burada documentation için ayrı bir promise dönüyor,
        // ama en sonda yine updatedData döneceğiz
        return Services.CreateDocumentation(
          useToken,
          collectionId,
          receivedData.apiId,
          api.apiTitle,
          parameters
        )
          .then((docRes) => {
            if (!docRes.data.status) {
              toastError(getTranslation("somethingWentWrong"));
            }

            getAllCollections();
            setSelectedApi(updatedData);
            toastSuccess(getTranslation("successMessage"));

            // 🔴 EN ÖNEMLİ NOKTA: en sonda updatedData dönüyoruz
            return updatedData;
          })
          .catch((err) => {
            console.log(err);
            // Doc yaratmada hata olsa bile API oluştu, updatedData'yı yine de döndürelim
            getAllCollections();
            setSelectedApi(updatedData);
            toastSuccess(getTranslation("successMessage"));
            return updatedData;
          });
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
        return null;
      });
  };

  const handleAddNewApi = (collectionId) => {
    setAddToCollectionId(collectionId);
    setSelectedApi({});
  };

  const handleDeleteApi = (apiId) => {
    Services.DeleteAPI(useToken, apiId)
      .then((res) => {
        if (res.data.status) {
          getAllCollections();
          // setSelectedApi({});
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log("catch: ", err));
  };

  const handleUpdateCollectionTitle = (collectionId, newName) => {
    Services.UpdateCollection(token, collectionId, newName)
      .then((res) => {
        if (res.data.status) {
          getAllCollections();
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleDeleteCollection = (collectionId) => {
    Services.DeleteCollection(token, collectionId)
      .then((res) => {
        if (res.data.status) {
          getAllCollections();
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
    if (selectedApi.collectionId === collectionId) {
      setSelectedApi({});
    }
  };

  // documentation
  const handleGetDocumentation = (apiId) => {
    Services.GetDocumentationByApiID(useToken, apiId)
      .then((res) => {
        if (res.data.status) {
          setSelectedDocumentation(res.data.result[0]);
        } else {
          setSelectedDocumentation({});
        }
      })
      .catch((err) => {
        console.log(err);
        setSelectedDocumentation({});
      });
  };

  const handleUpdateDocumentation = (apiId, id, updateData) => {
    Services.UpdateDocumentation(useToken, apiId, id, updateData)
      .then((res) => {
        if (res.data.status) {
          toastSuccess(getTranslation("successMessage"));
          setSelectedDocumentation({
            ...selectedDocumentation,
            parameters: res.data.result.parameters,
            description: res.data.result.description,
          });
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  // kronos
  const triggerKronosRefresh = () => {
    setKronosRefreshCounter((prev) => prev + 1);
  };

  const handleKronosCreateAndStart = async (timeLog) => {
    try {
      const response = await Services.KronosCreateAndStart(
        useToken,
        selectedProjectID,
        timeLog.title,
        // timeLog.members,
        timeLog.taskId ? timeLog.taskId : "",
        timeLog.subProjectId
      );

      if (response.data.status) {
        const startTime = Date.now();
        setActiveKronos({
          title: timeLog.title,
          startTime,
          projectTitle: selectedProjectTitle,
          kronosId: response?.data?.result?.kronosId,
          logId: response?.data?.result?.logId,
          taskId: timeLog.taskId ? timeLog.taskId : "",
        });
        return {
          status: true,
          result: {
            kronosId: response.data.result.kronosId,
            logId: response.data.result.logId,
          },
          timestamp: response.data.timestamp,
        };
      } else {
        // toastError(getTranslation("somethingWentWrong"));
        return { status: false };
      }
    } catch (error) {
      // toastError(getTranslation("somethingWentWrong"));
      console.error("Error in KronosCreateAndStart:", error);
      return { status: false };
    }
  };

  const handleKronosStop = async (logId) => {
    try {
      const response = await Services.KronosStop(useToken, logId);

      if (response.data.status) {
        setActiveKronos(null);
        if (timerInterval) {
          clearInterval(timerInterval);
        }
        return response.data;
      } else {
        // toastError(getTranslation("somethingWentWrong"));
        return { status: false };
      }
    } catch (error) {
      // toastError(getTranslation("somethingWentWrong"));
      console.error("Error in KronosCreateAndStart:", error);
      return { status: false };
    }
  };

  const handleKronosGetByToken = async (rangeData) => {
    const data = {
      projectID: selectedProjectID,
      filter: rangeData.type,
      ...(rangeData.type === "inBetween" && {
        startDate: rangeData.start,
        endDate: rangeData.end,
      }),
    };

    try {
      const response = await Services.GetMyKronoses(useToken, data);
      if (response.data.status) {
        return response.data;
      } else {
        // toastError(getTranslation("somethingWentWrong"));
        return { status: false };
      }
    } catch (error) {
      // toastError(getTranslation("somethingWentWrong"));
      console.error("Error in handleKronosGetByToken:", error);
      return { status: false };
    }
  };

  const handleGetCurrentKronos = async () => {
    try {
      const response = await Services.GetCurrentKronos(useToken);
      if (response?.data?.result) {
        const { kronosTitle, startDateTime, projectTitle, kronosId, logId } =
          response?.data?.result;
        setActiveKronos({
          title: kronosTitle,
          startTime: new Date(startDateTime * 1000).getTime(),
          projectTitle: projectTitle,
          kronosId: kronosId,
          logId: logId,
          taskId: activeKronos?.taskId ? activeKronos?.taskId : "",
        });
        return {
          status: true,
          result: response?.data?.result,
        };
      } else {
        return { status: false };
      }
    } catch (error) {
      // toastError(getTranslation("somethingWentWrong"));
      toastError(getTranslation("somethingWentWrong"));
      console.error("Error in KronosCreateAndStart:", error);
      return { status: false };
    }
  };

  const handleKronosDiscard = async (kronosId, logId) => {
    try {
      const response = await Services.KronosDiscard(useToken, kronosId, logId);
      if (response.data.status) {
        document.title = "lodos.";
        setActiveKronos(null);
        // toastSuccess(getTranslation("successMessage"));
        return { status: true };
      } else {
        // toastError(getTranslation("somethingWentWrong"));
        toastError(getTranslation("somethingWentWrong"));
        return { status: false };
      }
    } catch (error) {
      // toastError(getTranslation("somethingWentWrong"));
      toastError(getTranslation("somethingWentWrong"));
      return { status: false };
    }
  };

  const handleKronosDelete = async (kronosIds) => {
    try {
      const response = await Services.KronosDelete(useToken, kronosIds);
      if (response.data.status) {
        // toastSuccess(getTranslation("successMessage"));
        return { status: true };
      } else {
        // toastError(getTranslation("somethingWentWrong"));
        toastError(getTranslation("somethingWentWrong"));
        return { status: false };
      }
    } catch (error) {
      // toastError(getTranslation("somethingWentWrong"));
      toastError(getTranslation("somethingWentWrong"));
      return { status: false };
    }
  };

  const handleKronosLogDelete = async (logIds) => {
    try {
      const response = await Services.KronosLogsDelete(useToken, logIds);
      if (response.data.status) {
        // toastSuccess(getTranslation("successMessage"));
        return { status: true };
      } else {
        // toastError(getTranslation("somethingWentWrong"));
        toastError(getTranslation("somethingWentWrong"));
        return { status: false };
      }
    } catch (error) {
      // toastError(getTranslation("somethingWentWrong"));
      toastError(getTranslation("somethingWentWrong"));
      return { status: false };
    }
  };

  const handleKronosUpdate = async (kronosId, logId, updateData) => {
    try {
      const response = await Services.KronosUpdate(
        useToken,
        kronosId,
        logId,
        updateData
      );

      if (response.data.status) {
        // toastSuccess(getTranslation("successMessage"));
      } else {
        toastError(getTranslation("somethingWentWrong"));
      }
    } catch (error) {
      toastError(getTranslation("somethingWentWrong"));
      console.error("Error in Kronos update:", error);
    }
  };

  const handleManualKronosCreate = async (
    title,
    startDate,
    endDate,
    groupId,
    subProjectId
  ) => {
    // setIsLoading(true);
    try {
      const response = await Services.KronosCreateManual(
        useToken,
        title,
        selectedProjectID,
        startDate,
        endDate,
        groupId,
        subProjectId
      );

      if (response.data.status) {
        toastSuccess(getTranslation("successMessage"));
      } else {
        toastError(getTranslation("somethingWentWrong"));
      }
    } catch (error) {
      toastError(getTranslation("somethingWentWrong"));
      console.error("Error in manual create kronos:", error);
      return { status: false };
    }
  };

  const handleCreateKronosGroup = async (title) => {
    try {
      const response = await Services.CreateKronosGroup(
        useToken,
        title,
        selectedProjectID
      );
      if (response.data.status) {
        toastSuccess(getTranslation("successMessage"));
        return { status: true };
      } else {
        toastError(getTranslation("somethingWentWrong"));
        return { status: false };
      }
    } catch (error) {
      toastError(getTranslation("somethingWentWrong"));
      return { status: false };
    }
  };

  const handleUpdateKronosGroup = async (groupId, kronoses) => {
    try {
      const response = await Services.UpdateKronosGroup(
        useToken,
        groupId,
        kronoses
      );
      if (response.data.status) {
        toastSuccess(getTranslation("successMessage"));
        return { status: true };
      } else {
        toastError(getTranslation("somethingWentWrong"));
        return { status: false };
      }
    } catch (error) {
      return { status: false };
    }
  };

  const handleKronosStart = async (kronosId, title) => {
    try {
      const response = await Services.KronosStart(useToken, userId, kronosId);
      if (response.data.status) {
        const startTime = Date.now();
        setActiveKronos({
          title,
          startTime,
          projectTitle: selectedProjectTitle,
          kronosId: response?.data?.result.kronosId,
          logId: response?.data?.result.kronosId,
        });
        return {
          status: true,
          result: {
            title: title,
            kronosId: kronosId,
            logId: response.data.result.logId,
          },
          timestamp: response.data.timestamp,
        };
      } else {
        // toastError(getTranslation("somethingWentWrong"));
        return { status: false };
      }
    } catch (error) {
      // toastError(getTranslation("somethingWentWrong"));
      console.error("Error in KronosStart:", error);
      return { status: false };
    }
  };

  const handleBearerTokenChange = (token) => {
    setBearerToken(token);
  };

  const fetchOpenedWindows = async () => {
    try {
      const res = await Services.GetUserSettings(useToken);
      if (res.data.status && res.data.result.settings) {
        setOpenedWindows(
          res.data.result.settings.filter((x) => x.projectId !== "")
        );
      } else {
        setOpenedWindows([]);
      }
    } catch (err) {
      console.error("Error fetching openedWindows:", err);
      setOpenedWindows([]);
    }
  };

  const handleUpdateOpenedWindows = async (windows) => {
    try {
      const response = await Services.UpdateUserSettings(useToken, windows);
    } catch (err) {
      console.error("Error updating openedWindows:", err);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        Services.UpdateUserSettings(useToken, openedWindows);
      } catch (err) {
        console.error("Error updating openedWindows:", err);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [openedWindows]);

  const handleRemoveUserFromProject = (authID) => {
    Services.RemoveUserFromProject(useToken, authID)
      .then((res) => {
        if (res.data.status) {
          fetchMembers();
          handleGetSentProjectInvitations();
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const handleResendProjectInvitation = (authID) => {
    Services.ResendProjectInvite(useToken, authID)
      .then((res) => {
        if (res.data.status) {
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const handleResendCollaboratorInvitation = (invitationCode) => {
    Services.ResendCollaboratorInvite(useToken, invitationCode)
      .then((res) => {
        if (res.data.status) {
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.log(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const getMyNotes = () => {
    handleGetRecentNotes();
    Services.GetMyNotes(token)
      .then((res) => {
        if (res.data.status) {
          const folderNotes =
            res.data.result.folders?.flatMap(
              (folder) =>
                folder.notes?.map((note) => ({
                  ...note,
                  folderTitle: folder.title,
                })) || []
            ) || [];

          const ungroupedNotes = res.data.result.notes || [];
          const allNotes = [...folderNotes, ...ungroupedNotes];
          setUnfolderedNotes(allNotes);
          setMyNotes(res.data.result);
        } else {
          setUnfolderedNotes([]);
          setMyNotes([]);
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const getMyNotesAndSelect = (newNoteId) => {
    handleGetRecentNotes();
    Services.GetMyNotes(token)
      .then((res) => {
        if (res.data.status) {
          const folderNotes =
            res.data.result.folders?.flatMap(
              (folder) =>
                folder.notes?.map((note) => ({
                  ...note,
                  folderTitle: folder.title,
                })) || []
            ) || [];

          const ungroupedNotes = res.data.result.notes || [];
          const allNotes = [...folderNotes, ...ungroupedNotes];

          const newSelectedNote = allNotes.find(
            (note) => note.noteId === newNoteId
          );
          setUnfolderedNotes(allNotes);
          setMyNotes(res.data.result);
          setSelectedNote(newSelectedNote);
          handleTouchNote(newNoteId);
        } else {
          setUnfolderedNotes([]);
          setMyNotes([]);
          setSelectedNote(null);
          handleTouchNote("");
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleCreateNoteFolder = (title) => {
    Services.AddNoteFolder(token, title)
      .then((res) => {
        if (res.data.status) {
          getMyNotes();
          toastSuccess(getTranslation("successMessage"));
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleCreateNote = (folderId, callBackFunc) => {
    setCreateProjectLoading(true)
    const idToSend =
      typeof folderId === "string" || typeof folderId === "number"
        ? folderId
        : folderId?.id;
    Services.CreateNote(token, idToSend || "")
      .then((res) => {
        setCreateProjectLoading(false)
        if (res.data.status) {
          getMyNotesAndSelect(res.data.result.noteId);
          callBackFunc(res.data.result.noteId);
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleDeleteNoteTemporary = (noteId) => {
    setCreateProjectLoading(true)
    Services.DeleteNoteTemporary(token, noteId)
      .then((res) => {
        setCreateProjectLoading(false)
        if (res.data.status) {
          getMyNotes();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleUpdateNote = (noteId, content) => {
    Services.NoteUpdate(token, noteId, content)
      .then((res) => {
        if (res.data.status) {
          getMyNotes();
        } else {
          if (res.data.message !== "note not found") {
            toastError(getTranslation("somethingWentWrong"));
          }
        }
      })
      .catch((err) => console.log(err));
  };

  const handleUpdateNoteTitle = (noteId, title) => {
    Services.NoteTitleUpdate(token, noteId, title)
      .then((res) => {
        if (res.data.status) {
          getMyNotesAndSelect(noteId);
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleMoveNoteFolder = (noteId, folderId) => {
    Services.NoteMoveFolder(token, noteId, folderId)
      .then((res) => {
        if (res.data.status) {
          getMyNotesAndSelect(noteId);
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleStarNote = (noteId) => {
    Services.NoteStar(token, noteId)
      .then((res) => {
        if (res.data.status) {
          getMyNotesAndSelect(noteId);
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleUnstarNote = (noteId) => {
    Services.NoteUnstar(token, noteId)
      .then((res) => {
        if (res.data.status) {
          getMyNotesAndSelect(noteId);
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleTouchNote = (noteId) => {
    Services.TouchToNote(token, noteId)
      .then((res) => {
        if (res.data.status) {
          getMyNotes();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleMenuStarNote = (noteId) => {
    Services.NoteStar(token, noteId)
      .then((res) => {
        if (res.data.status) {
          if (selectedNote) {
            if (selectedNote.noteId === noteId) {
              getMyNotesAndSelect(noteId);
            } else {
              getMyNotes();
            }
          } else {
            getMyNotes();
          }
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleMenuUnstarNote = (noteId) => {
    Services.NoteUnstar(token, noteId)
      .then((res) => {
        if (res.data.status) {
          if (selectedNote) {
            if (selectedNote.noteId === noteId) {
              getMyNotesAndSelect(noteId);
            } else {
              getMyNotes();
            }
          } else {
            getMyNotes();
          }
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleGetRecentNotes = () => {
    Services.GetRecentNotes(token)
      .then((res) => {
        if (res.data.status) {
          setRecentNotes(res.data.result);
        } else {
          setRecentNotes([]);
        }
      })
      .catch((err) => console.log(err));
  };

  const handleUpdateNoteFolderTitle = (id, title) => {
    Services.UpdateNoteFolder(token, id, title)
      .then((res) => {
        if (res.data.status) {
          getMyNotes();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleDeleteNoteFolder = (id) => {
    setCreateProjectLoading(true)
    Services.TemporaryDeleteNoteFolder(token, id)
      .then((res) => {
        setCreateProjectLoading(false)
        if (res.data.status) {
          getMyNotes();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const getMyChannels = () => {
    if (!token) {
      setMyChannels([]);
      // Her zaman bir Promise dönelim ki çağıran taraf .finally kullanabilsin
      return Promise.resolve([]);
    }

    return Services.SocialHiveGetMyChannels(token)
      .then((res) => {
        if (res?.data?.status && Array.isArray(res?.data?.result)) {
          setMyChannels(res.data.result);
          return res.data.result; // isteyen yerler list'e de erişebilir
        } else {
          setMyChannels([]);
          return [];
        }
      })
      .catch((err) => {
        console.log(err);
        setMyChannels([]);
        return [];
      });
  };

  const handleCreateChannel = (title, description, isPublic, logo, topBanner) => {
    Services.SocialHiveCreateChannel(token, title, description, isPublic, logo, topBanner)
      .then((res) => {
        if (res.data.status) {
          getMyChannels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleUpdateChannel = (
    channelID,
    title,
    description,
    isPublic,
    logo,
    topBanner
  ) => {
    Services.SocialHiveUpdateChannel(
      token,
      channelID,
      title,
      description,
      isPublic,
      logo,
      topBanner
    )
      .then((res) => {
        if (res.data.status) {
          toastSuccess(getTranslation("successMessage"));
          getMyChannels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const getChannelRooms = (channelID) => {
    return Services.SocialHiveGetChannelRooms(token, channelID)
      .then((res) => {
        if (res.data.status) {
          setChannelRooms(res.data.result);
          const roomList = res.data.result;
          setOrderedRooms(roomList);
          return res.data.result; // 🔁 sıralama için geri dön
        } else {
          setChannelRooms([]);
          return [];
        }
      })
      .catch((err) => {
        console.log(err);
        return [];
      });
  };

  const handleCreateRoom = (
    channelID,
    title,
    description,
    isPublic,
    canUsersSendMessage
  ) => {
    const token = localStorage.getItem("token");
    return Services.SocialHiveCreateRoom(
      token,
      channelID,
      title,
      description,
      isPublic,
      canUsersSendMessage
    )
      .then((res) => {
        if (res.data.status) {
          getChannelRooms(channelID);
          return res.data.result; // ✅ yeni odayı döndür
        } else {
          toastError(getTranslation("somethingWentWrong"));
          return null;
        }
      })
      .catch((err) => {
        console.log(err);
        return null;
      });
  };

  const handleLeaveRoom = (channelID, roomID) => {
    const userID = sessionUser ? sessionUser?.id : localUser?.id;
    Services.SocialHiveLeaveRoom(token, roomID, userID)
      .then((res) => {
        if (res.data.status) {
          getChannelRooms(channelID);
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleJoinRoom = (channelID, roomID) => {
    const userID = sessionUser ? sessionUser?.id : localUser?.id;
    Services.SocialHiveJoinRoom(token, roomID, userID, channelID)
      .then((res) => {
        if (res.data.status) {
          getChannelRooms(channelID);
        } else {
          if (res.data.message !== "User already exists") {
            toastError(getTranslation("somethingWentWrong"));
          }
        }
      })
      .catch((err) => console.log(err));
  };

  const handleDeleteChannel = (channelID) => {
    Services.SocialHiveDeleteChannel(token, channelID)
      .then((res) => {
        if (res.data.status) {
          getMyChannels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleJoinChannel = (channelID) => {
    setCreateProjectLoading(true);
    const userID = sessionUser ? sessionUser?.id : localUser?.id;
    Services.SocialHiveJoinToChannel(token, userID, channelID)
      .then((res) => {
        if (res.data.status) {
          setShChannelUri("");
          getMyChannels();
        } else {
          if (res?.data?.message?.includes("already")) {
            toastWarning(getTranslation("alreadyRequestedToJoinChannel"));
          } else {
            toastError(getTranslation("somethingWentWrong"));
          }
        }
        setCreateProjectLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setCreateProjectLoading(false);
      });
  };

  const handleLeaveChannel = (channelID) => {
    const userID = sessionUser ? sessionUser?.id : localUser?.id;
    Services.SocialHiveLeaveChannel(token, userID, channelID)
      .then((res) => {
        if (res.data.status) {
          getMyChannels();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => console.log(err));
  };

  return (
    <GeneralContext.Provider
      value={{
        useToken,
        projects,
        subProjects,
        setSubProjects,
        getProjectMembers,
        handleTokenUpdate,
        handleGoogleLogin,
        handleGithubLogin,
        handleAddNewProject,
        handleAddSubProject,
        handleUpdateProjectTitle,
        handleUpdateSubProject,
        selectedProjectID,
        setSelectedProjectID,
        selectedProjectTitle,
        handleSelectProject,
        handleUpdateOpenedWindows,
        getTempOpenedWindows: () => tempOpenedWindows.current,
        // handleGetOpenedWindows,
        collections,
        setCollections,
        addToCollectionId,
        handleAddNewApi,
        selectedApi,
        setSelectedApi,
        handleAddApi,
        bearerToken,
        handleBearerTokenChange,
        handleDeleteCollection,
        handleUpdateCollectionTitle,
        handleChangeApiGroup,
        handleUpdateApi,
        handleDeleteApi,
        handleSelectApi,
        handleAddNewCollection,
        handleInviteCollaborators,
        handleRemoveCollaborator,
        handleGetCollaborators,
        collaborators,
        receivedInvitations,
        handleGetReceivedInvitations,
        handleGetSentInvitations,
        handleCancelCollaboratorInvitation,
        handleGetRecievedProjectInvitations,
        handleGetSentProjectInvitations,
        receivedProjectInvitations,
        sentProjectInvitations,
        sentInvitations,
        handleAcceptInvitation,
        handleRejectInvitation,
        handleDeleteProject,
        handleDeleteSubProject,
        handleCreateAuthorization,
        handleInviteToProject,
        handleAddMembers,
        getUserAuthorizations,
        getMembers,
        userId,
        handleGetDocumentation,
        selectedDocumentationID,
        setSelectedDocumentationID,
        selectedDocumentation,
        setSelectedDocumentation,
        handleUpdateDocumentation,
        handleSaveAsApi,
        getAllPanels,
        handleUpdatePanelTitle,
        handleDeletePanel,
        handleAddNewTask,
        handleDeleteTask,
        handleUpdateTask,
        panels,
        handleAddNewPanel,
        handleNewAddBoard,
        handleDeleteBoard,
        handleBoardUpdate,
        getCollaboratorsByProjectID,
        handleAcceptProjectInvitation,
        handleRejectProjectInvitation,
        // kronos
        handleKronosCreateAndStart,
        handleKronosStop,
        handleKronosGetByToken,
        handleKronosDiscard,
        handleKronosDelete,
        handleKronosUpdate,
        activeKronos,
        handleGetCurrentKronos,
        handleManualKronosCreate,
        handleCreateKronosGroup,
        handleUpdateKronosGroup,
        handleKronosStart,
        handleKronosLogDelete,
        getCollaboratorsKronoses,
        kronosRefreshCounter,
        triggerKronosRefresh,
        // opened windows
        openedWindows,
        setOpenedWindows,
        tabs,
        setTabs,
        selectedTab,
        setSelectedTab,
        updateCollaboratorsAuthorizations,
        fetchMembers,
        users,
        setUsers,
        handleRemoveUserFromProject,
        setEmailInvitationCode,
        emailInvitationCode,
        handleAcceptInvitationWithToken,
        handleRejectInvitationWithToken,
        handleCollaboratorInvitationByEmail,
        setEmailInvitationAcceptReject,
        handleAcceptProjectInvitationWithToken,
        handleRejectProjectInvitationWithToken,
        setEmailInvitationType,
        handleResendProjectInvitation,
        handleResendCollaboratorInvitation,
        selectedTaskByKronos,
        setSelectedTaskByKronos,
        handleWriteComment,
        handleDeleteComment,
        handleUpdateComment,
        handleDeleteAttachment,
        handleDeleteCommentAttachment,
        handleAddTaskAttachment,
        handleAddCommentAttachment,
        setPanelSelectedYear,
        setPanelSelectedMonth,
        handleCreateApiGroup,
        handleUpdateApiGroup,
        handleDeleteApiGroup,
        handleAddMemberToTask,
        handleRemoveMemberFromTask,
        handleCreateNoteFolder,
        getMyNotes,
        myNotes,
        handleCreateNote,
        handleDeleteNoteTemporary,
        unfolderedNotes,
        setSelectedNote,
        selectedNote,
        handleUpdateNote,
        handleTouchNote,
        recentNotes,
        handleUpdateNoteTitle,
        handleUpdateNoteFolderTitle,
        handleDeleteNoteFolder,
        setIsSubscriptionExpired,
        isSubscriptionExpired,
        subscriptionRenewDate,
        setSubscriptionRenewDate,
        handleMoveNoteFolder,
        handleStarNote,
        handleUnstarNote,
        handleUpdateApiTitle,
        handleMenuStarNote,
        handleMenuUnstarNote,
        handleCreateChannel,
        getMyChannels,
        myChannels,
        getChannelRooms,
        channelRooms,
        handleCreateRoom,
        handleDeleteChannel,
        handleUpdateChannel,
        handleLeaveRoom,
        handleJoinRoom,
        handleJoinChannel,
        handleLeaveChannel,
        userInformation,
        setCreateProjectLoading,
        subscriptionType,
        setSubscriptionType,
        setSubscriptionToggle,
        subscriptionToggle,
        shChannelUri,
        setShChannelUri,
        dmRoomUri,
        setDmRoomUri,
        orderedRooms,
        setOrderedRooms,
        selectedGame,
        startGame,
        endGame,
        stopGamePing,
        eventIDUri,
        setEventIDUri,
        eventUriTab,
        setEventUriTab,
        buildUserDetails,
        sendEventVisit,
        resetShAnalytics,
        shAnalyticsRef,
        setSuppressInvitationPolling,
        getAllCollections,
        showOnboardingModal,
        setShowOnboardingModal,
        isFirstLogin
      }}
    >
      {createProjectLoading && <LoadingOverlay />}
      <OnboardingVideoModal 
        isOpen={showOnboardingModal} 
        onClose={() => setShowOnboardingModal(false)}
        isFirstLogin={isFirstLogin}
      />
      {children}
    </GeneralContext.Provider>
  );
};

export { GeneralProvider, GeneralContext };
